/**
 * Edit-mode toolbar: floats over the sheet, so the page keeps the whole screen while writing (the
 * song page hides its top bar and tab strip in edit mode). Two linked cards move together: the tools
 * — with undo / redo, layers, page turning (‹ 3 / 8 ›, any tool stays active) and "Lokið" — against
 * the edge, and the current tool's options (colours, widths, size, hint, save state) beside them on
 * the page side. Docked to an edge (dock.ts): lying along the top / bottom (tool labels from 1024 px,
 * so a portrait iPad fits it all), standing at the left / right (icons only, options in a column).
 * The grip and "Lokið" never scroll out of reach. Drag the grip and it snaps to the edge nearest the
 * finger; its arrow keys move it too. Remembered per device. It reports how much of each side it
 * covers (onInsets) so the selection bar and banners stay clear of it.
 */
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  Eraser,
  GripHorizontal,
  GripVertical,
  Highlighter,
  Layers,
  MousePointer2,
  Music,
  PenLine,
  Redo2,
  Type,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, type CSSProperties } from 'react';
import { cx } from '../components/ui';
import { useT, type MessageKey } from '../i18n';
import { DEFAULT_DOCK, DOCK_STORAGE, type DockEdge } from '../lib/dock';
import { useFloatingDock, type DockInsets } from '../lib/useFloatingDock';
import { SyncIndicator } from './SyncIndicator';
import { ToolOptions } from './ToolOptions';
import type { ToolId } from './tools';
import type { Annotate } from './useAnnotate';

const TOOLS: { id: ToolId; icon: LucideIcon; label: MessageKey }[] = [
  { id: 'pen', icon: PenLine, label: 'annotate.tool_pen' },
  { id: 'highlighter', icon: Highlighter, label: 'annotate.tool_highlighter' },
  { id: 'text', icon: Type, label: 'annotate.tool_text' },
  { id: 'stamp', icon: Music, label: 'annotate.tool_stamp' },
  { id: 'connector', icon: CornerDownRight, label: 'annotate.tool_connector' },
  { id: 'eraser', icon: Eraser, label: 'annotate.tool_eraser' },
  { id: 'select', icon: MousePointer2, label: 'annotate.tool_select' },
];

/** Cards in DOM order [tools, options]; the options always sit on the page side of the tools. */
const DIRECTION: Record<DockEdge, CSSProperties['flexDirection']> = { top: 'column', bottom: 'column-reverse', left: 'row', right: 'row-reverse' };

/** How much of each side of the container the toolbar covers (0 for the sides it is not docked to). */
export type ToolbarInsets = DockInsets;

const CARD = 'pointer-events-auto rounded-2xl bg-[var(--surface)] p-1 shadow-[0_6px_24px_rgb(0_0_0/0.22)] ring-1 ring-[var(--line)]';

function ToolButton({
  icon: Icon,
  label,
  active,
  disabled,
  onClick,
  pressed,
  dataTool,
  iconOnly,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  pressed?: boolean;
  dataTool?: string;
  /** Icon only (standing toolbar); otherwise the label shows from 1024 px. It stays for screen readers and as the tooltip. */
  iconOnly?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      data-tool={dataTool}
      title={label}
      className={cx(
        'flex size-11 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl text-[13px] leading-tight font-semibold transition disabled:opacity-35',
        !iconOnly && 'lg:h-auto lg:min-h-14 lg:w-auto lg:min-w-[60px] lg:px-1.5',
        active ? 'bg-[var(--accent)] text-[var(--accent-ink)] shadow-sm' : 'text-[var(--ink-2)] hover:bg-[var(--accent-soft)] active:bg-[var(--accent-soft)]',
      )}
    >
      <Icon className="size-6" aria-hidden />
      <span className={cx('whitespace-nowrap', iconOnly ? 'sr-only' : 'sr-only lg:not-sr-only')}>{label}</span>
    </button>
  );
}

const Sep = ({ vertical }: { vertical: boolean }) => (
  <span className={cx('shrink-0 self-center bg-[var(--line)]', vertical ? 'my-0.5 h-px w-9' : 'mx-0.5 h-9 w-px')} aria-hidden />
);

export function Toolbar({ ann, onDone, onInsets }: { ann: Annotate; onDone?: () => void; onInsets?: (insets: ToolbarInsets) => void }) {
  const t = useT();
  const { engine } = ann;
  const floating = useFloatingDock<HTMLDivElement>({ storageKey: DOCK_STORAGE, fallback: DEFAULT_DOCK });
  const { dock, vertical, dragging, insets } = floating;
  const { top, bottom, left, right } = insets;
  useEffect(() => {
    onInsets?.({ top, bottom, left, right });
  }, [onInsets, top, bottom, left, right]);

  const pageCount = ann.file.pageCount ?? ann.pages.length;
  const page = Math.min(ann.currentPage, Math.max(0, pageCount - 1));
  // Erase / select have no options but a hint, which a standing toolbar leaves out.
  const hasOptions = !(vertical && (ann.tool === 'eraser' || ann.tool === 'select'));
  const scroll = vertical ? 'min-h-0 overflow-y-auto' : 'min-w-0 overflow-x-auto';
  const stack = vertical ? 'flex-col' : 'flex-row';

  return (
    <div
      ref={floating.ref}
      role="toolbar"
      aria-label={t('annotate.toolbar_label')}
      data-dock={dock.edge}
      className={cx('pointer-events-none absolute top-0 left-0 z-40 flex gap-1.5', vertical ? 'items-start' : 'items-center')}
      style={{ ...floating.style, flexDirection: DIRECTION[dock.edge] }}
    >
      <div className={cx(CARD, 'flex max-h-full max-w-full items-center gap-0.5', stack)}>
        <button
          type="button"
          aria-label={t('annotate.move_toolbar')}
          title={t('annotate.move_toolbar_hint')}
          aria-describedby="kgr-move-toolbar-hint"
          {...floating.gripProps}
          data-grip
          className={cx(
            'flex shrink-0 cursor-grab touch-none items-center justify-center rounded-xl text-[var(--muted)] hover:bg-[var(--surface-2)] active:cursor-grabbing',
            vertical ? 'h-11 w-11' : 'h-11 w-11 lg:h-14',
            dragging && 'bg-[var(--accent-soft)] text-[var(--accent)]',
          )}
        >
          {vertical ? <GripHorizontal className="size-6" aria-hidden /> : <GripVertical className="size-6" aria-hidden />}
        </button>
        <span id="kgr-move-toolbar-hint" className="sr-only">
          {t('annotate.move_toolbar_hint')}
        </span>
        {/* Only this part scrolls when the screen is too small: the grip and "Lokið" stay in reach. */}
        <div className={cx('flex items-center gap-0.5', stack, scroll)}>
          {TOOLS.map((tool) => (
            <ToolButton
              key={tool.id}
              dataTool={tool.id}
              icon={tool.icon}
              label={t(tool.label)}
              active={ann.tool === tool.id}
              pressed={ann.tool === tool.id}
              onClick={() => ann.setTool(tool.id)}
              iconOnly={vertical}
            />
          ))}
          <Sep vertical={vertical} />
          <ToolButton icon={Undo2} label={t('annotate.undo')} disabled={!engine.canUndo} onClick={ann.undo} iconOnly />
          <ToolButton icon={Redo2} label={t('annotate.redo')} disabled={!engine.canRedo} onClick={ann.redo} iconOnly />
          <ToolButton icon={Layers} label={t('annotate.layers')} dataTool="layers" onClick={() => ann.setLayersOpen(true)} iconOnly={vertical} />
          <Sep vertical={vertical} />
          <div role="group" aria-label={t('annotate.pages_label')} className={cx('flex shrink-0 items-center', stack)}>
            <button
              type="button"
              onClick={() => ann.turnPage(-1)}
              disabled={page <= 0}
              aria-label={t('annotate.prev_page')}
              title={t('annotate.prev_page')}
              className="flex size-11 items-center justify-center rounded-xl text-[var(--ink-2)] hover:bg-[var(--accent-soft)] disabled:opacity-35"
            >
              <ChevronLeft className={cx('size-6', vertical && 'rotate-90')} aria-hidden />
            </button>
            <span className="min-w-11 text-center text-[14px] font-semibold text-[var(--muted)] tabular-nums">
              <span aria-hidden>{t('viewer.page_indicator', { page: page + 1, count: pageCount })}</span>
              <span className="sr-only">{t('viewer.page_aria', { page: page + 1, count: pageCount })}</span>
            </span>
            <button
              type="button"
              onClick={() => ann.turnPage(1)}
              aria-label={t('annotate.next_page')}
              title={t('annotate.next_page')}
              className="flex size-11 items-center justify-center rounded-xl text-[var(--ink-2)] hover:bg-[var(--accent-soft)]"
            >
              <ChevronRight className={cx('size-6', vertical && 'rotate-90')} aria-hidden />
            </button>
          </div>
          {vertical ? (
            <>
              <Sep vertical />
              <SyncIndicator sync={engine.sync} iconOnly />
            </>
          ) : null}
        </div>
        {onDone ? (
          <button
            type="button"
            onClick={onDone}
            title={t('annotate.done_hint')}
            className={cx(
              'ml-0.5 inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-[var(--ok)] font-bold text-white shadow-sm transition active:scale-95',
              vertical ? 'mt-0.5 ml-0 size-11' : 'min-h-11 px-3 text-[16px] lg:min-h-14',
            )}
          >
            <Check className="size-5" strokeWidth={3} aria-hidden />
            <span className={vertical ? 'sr-only' : undefined}>{t('annotate.done')}</span>
          </button>
        ) : null}
      </div>
      {hasOptions ? (
        <div aria-label={t('annotate.options_label')} role="group" className={cx(CARD, 'flex max-h-full max-w-full items-center', stack, scroll)}>
          <ToolOptions prefs={ann.prefs} update={ann.updatePrefs} onOpenPalette={() => ann.setPaletteOpen(true)} vertical={vertical} />
          {vertical ? null : (
            <>
              <Sep vertical={false} />
              <SyncIndicator sync={engine.sync} compact />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
